import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { fetchHelpContent, fetchHelpState, patchHelpState } from './helpApi';
import { canRead, primaryHelpRole, viewerHelpRoles, type HelpRole } from './helpRoles';
import { matchRoute } from './routeMatch';
import { TOURS, WELCOME_TOUR, getTour } from './tours';
import type { HelpContent, HelpEntry, HelpState, TourDef } from './types';
import HelpPanel from './HelpPanel';
import TourRunner from './TourRunner';
import WelcomeCard from './WelcomeCard';
import { useLegalNoticeShowing } from '../features/legal/noticeStore';

/** What the panel is showing. */
export type HelpView =
  | { name: 'home' }
  | { name: 'topic'; id: string }
  | { name: 'news' }
  | { name: 'shortcuts' }
  | { name: 'edit'; id: string | null; kind: 'topic' | 'news' };

const EMPTY_STATE: HelpState = { tours: {}, welcome_dismissed: false, whats_new_seen_at: null };
/** A What's new entry older than this never counts as unread, so a new account is not greeted by a pile of dots. */
const UNREAD_WINDOW_DAYS = 60;
const cacheKey = (userId: string) => `kudos.helpState.${userId}`;

function readCache(userId: string | undefined): HelpState | null {
  if (!userId) return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(userId));
    return raw ? { ...EMPTY_STATE, ...JSON.parse(raw) } : null;
  } catch {
    return null;
  }
}

function writeCache(userId: string | undefined, state: HelpState) {
  if (!userId) return;
  try { window.localStorage.setItem(cacheKey(userId), JSON.stringify(state)); } catch { /* private mode */ }
}

/** The tour's own status for this person: never taken, taken, or taken before it changed. */
export function tourStatus(tour: TourDef, state: HelpState): 'new' | 'done' | 'changed' {
  const record = state.tours[tour.id];
  if (!record) return 'new';
  return record.version < tour.version ? 'changed' : 'done';
}

export function unreadNews(news: HelpEntry[], seenAt: string | null, today = new Date()): HelpEntry[] {
  const floor = new Date(today.getTime() - UNREAD_WINDOW_DAYS * 86400000).toISOString().slice(0, 10);
  const seen = seenAt ? seenAt.slice(0, 10) : null;
  return news.filter((n) => !!n.date && n.date >= floor && (!seen || n.date > seen));
}

export interface HelpContextValue {
  state: {
    open: boolean;
    view: HelpView;
    content: HelpContent | null;
    loading: boolean;
    failed: boolean;
    helpState: HelpState;
    unreadCount: number;
    activeTourId: string | null;
  };
  actions: {
    openPanel: (view?: HelpView) => void;
    closePanel: () => void;
    togglePanel: () => void;
    setView: (view: HelpView) => void;
    openTopic: (id: string) => void;
    startTour: (id: string) => void;
    finishTour: (id: string, status: 'done' | 'skipped') => void;
    /** Stops a tour that could not start, recording nothing. */
    cancelTour: () => void;
    dismissWelcome: () => void;
    markNewsSeen: () => void;
    reload: () => Promise<void>;
  };
  meta: {
    user: any;
    roles: HelpRole[];
    pathname: string;
    /** Tours this person may take, in WELCOME order. */
    tours: TourDef[];
  };
}

const HelpContext = createContext<HelpContextValue | null>(null);

/** The help system, or null outside it (a page rendered on its own in a test, the sign-in page). */
export function useHelp(): HelpContextValue | null {
  return useContext(HelpContext);
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

interface ProviderProps {
  user: any;
  /** Someone viewing as another person: help works, but nothing is saved to that person's account. */
  readOnly?: boolean;
  children: ReactNode;
}

/**
 * The help system: one button in every header, a side panel, the tours and the
 * first-visit card. No page and no menu entry -- it all hangs off the button
 * (and the ? key).
 */
export function HelpProvider({ user, readOnly = false, children }: ProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const userId: string | undefined = user?.id;
  const roles = useMemo(() => viewerHelpRoles(user), [user]);
  const rolesRef = useRef(roles);
  rolesRef.current = roles;

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<HelpView>({ name: 'home' });
  const [content, setContent] = useState<HelpContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [helpState, setHelpState] = useState<HelpState>(() => readCache(userId) || EMPTY_STATE);
  const [stateLoaded, setStateLoaded] = useState(false);
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const requestedRef = useRef(false);

  const reload = useCallback(async () => {
    if (!userId) return;
    requestedRef.current = true;
    setLoading(true);
    setFailed(false);
    try {
      const data = await fetchHelpContent();
      // The server already filters by role; this keeps a stale or wrong answer from showing more.
      setContent({
        topics: (data?.topics || []).filter((x) => canRead(rolesRef.current, x.roles || [])),
        news: data?.news || [],
        can_create: !!data?.can_create,
      });
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // A new person (or none): forget what the last one had.
  useEffect(() => {
    setContent(null);
    requestedRef.current = false;
    setHelpState(readCache(userId) || EMPTY_STATE);
    setStateLoaded(false);
    if (!userId) return undefined;
    let alive = true;
    fetchHelpState()
      .then((s) => {
        if (!alive) return;
        const next = { ...EMPTY_STATE, ...s, tours: { ...(s?.tours || {}) } };
        setHelpState(next);
        writeCache(userId, next);
        setStateLoaded(true);
      })
      .catch(() => { if (alive) setStateLoaded(false); });
    // What's new decides the dot on the button, so the content loads soon after sign-in,
    // once the page itself has had its turn.
    const timer = window.setTimeout(() => { if (!requestedRef.current) reload(); }, 1500);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [userId, reload]);

  const updateState = useCallback((change: (s: HelpState) => HelpState, patch: Partial<HelpState>) => {
    setHelpState((prev) => {
      const next = change(prev);
      writeCache(userId, next);
      return next;
    });
    if (!readOnly) patchHelpState(patch).catch(() => { /* the cache keeps it for this browser */ });
  }, [readOnly, userId]);

  const openPanel = useCallback((next?: HelpView) => {
    const active = document.activeElement as HTMLElement | null;
    if (active && active !== document.body && !active.closest('[data-help-panel]')) returnFocusRef.current = active;
    if (next) setView(next);
    setOpen(true);
    if (!requestedRef.current) reload();
  }, [reload]);

  const closePanel = useCallback(() => {
    setOpen(false);
    const back = returnFocusRef.current;
    window.requestAnimationFrame(() => {
      const target = back && document.contains(back) ? back : document.querySelector<HTMLElement>('[data-help-button]');
      target?.focus();
    });
  }, []);

  const togglePanel = useCallback(() => {
    if (open) closePanel(); else openPanel();
  }, [open, closePanel, openPanel]);

  const openTopic = useCallback((id: string) => openPanel({ name: 'topic', id }), [openPanel]);

  const startTour = useCallback((id: string) => {
    const tour = getTour(id);
    if (!tour) return;
    setOpen(false);
    if (!matchRoute(tour.route, location.pathname)) navigate(tour.route);
    setActiveTourId(id);
  }, [location.pathname, navigate]);

  const finishTour = useCallback((id: string, status: 'done' | 'skipped') => {
    setActiveTourId(null);
    const tour = getTour(id);
    if (!tour) return;
    const record = { version: tour.version, status };
    updateState((s) => ({ ...s, welcome_dismissed: true, tours: { ...s.tours, [id]: record } }),
      { welcome_dismissed: true, tours: { [id]: record } });
  }, [updateState]);

  const cancelTour = useCallback(() => setActiveTourId(null), []);

  const tours = useMemo(() => {
    const order = ['member', 'hr', 'admin'];
    return Object.values(TOURS)
      .filter((tour) => canRead(roles, tour.roles))
      .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  }, [roles]);

  const welcomeTour = user ? getTour(WELCOME_TOUR[primaryHelpRole(user)]) : null;

  const dismissWelcome = useCallback(() => {
    if (!welcomeTour) return;
    // The card only shows while this version of the tour is untaken, so nothing "done" is overwritten.
    // It stays in the panel's "On this page" for whenever they want it.
    const record = { version: welcomeTour.version, status: 'skipped' as const };
    updateState((s) => ({ ...s, welcome_dismissed: true, tours: { ...s.tours, [welcomeTour.id]: record } }),
      { welcome_dismissed: true, tours: { [welcomeTour.id]: record } });
  }, [updateState, welcomeTour]);

  const markNewsSeen = useCallback(() => {
    const now = new Date().toISOString();
    updateState((s) => ({ ...s, whats_new_seen_at: now }), { whats_new_seen_at: now });
  }, [updateState]);

  // ? (Shift+/) opens and closes help anywhere except while typing.
  useEffect(() => {
    if (!userId) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      if (isTyping(e.target) || isTyping(document.activeElement)) return;
      e.preventDefault();
      togglePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [togglePanel, userId]);

  const unreadCount = useMemo(
    () => (content ? unreadNews(content.news, helpState.whats_new_seen_at).length : 0),
    [content, helpState.whats_new_seen_at],
  );

  const value = useMemo<HelpContextValue>(() => ({
    state: { open, view, content, loading, failed, helpState, unreadCount, activeTourId },
    actions: { openPanel, closePanel, togglePanel, setView, openTopic, startTour, finishTour, cancelTour, dismissWelcome, markNewsSeen, reload },
    meta: { user, roles, pathname: location.pathname, tours },
  }), [open, view, content, loading, failed, helpState, unreadCount, activeTourId, openPanel, closePanel, togglePanel,
    openTopic, startTour, finishTour, cancelTour, dismissWelcome, markNewsSeen, reload, user, roles, location.pathname, tours]);

  const isAuthRoute = location.pathname === '/login' || location.pathname === '/signup';
  // The privacy notice comes first; the tour offer waits until it is answered.
  const legalNoticeShowing = useLegalNoticeShowing();
  const welcomeStatus = welcomeTour ? tourStatus(welcomeTour, helpState) : 'done';
  const showWelcome = !!user && !readOnly && stateLoaded && !open && !activeTourId && !isAuthRoute && !!welcomeTour
    && !legalNoticeShowing
    && ((welcomeStatus === 'new' && !helpState.welcome_dismissed) || welcomeStatus === 'changed');

  return (
    <HelpContext.Provider value={value}>
      {children}
      {user && <HelpPanel />}
      {activeTourId && <TourRunner key={activeTourId} tourId={activeTourId} />}
      {showWelcome && welcomeTour && <WelcomeCard tour={welcomeTour} changed={welcomeStatus === 'changed'} />}
    </HelpContext.Provider>
  );
}
